import { Controller } from "@hotwired/stimulus"

/**
 * Standalone zoom controller — button that opens a popup with slider.
 *
 * Dispatches an "editor-zoom:change" CustomEvent (bubbles) with { detail: { zoom } }
 * whenever the zoom level changes. Parent controllers listen for this event
 * and apply the zoom however they need.
 */
export default class extends Controller {
  static targets = ["slider", "popup", "buttonLabel"]

  static values = {
    min:  { type: Number, default: 0.5 },
    max:  { type: Number, default: 3 },
    step: { type: Number, default: 0.25 },
  }

  connect() {
    this._zoom = 1
    this._onClickOutside = (e) => {
      if (this._isOpen() && !this.element.contains(e.target)) this._close()
    }
    document.addEventListener("pointerdown", this._onClickOutside, true)
  }

  disconnect() {
    document.removeEventListener("pointerdown", this._onClickOutside, true)
  }

  togglePopup() {
    this._isOpen() ? this._close() : this._open()
  }

  onInput() {
    this._setZoom(parseFloat(this.sliderTarget.value))
  }

  zoomIn() {
    this._setZoom(Math.min(this.maxValue, this._zoom + this.stepValue))
  }

  zoomOut() {
    this._setZoom(Math.max(this.minValue, this._zoom - this.stepValue))
  }

  reset() {
    this._setZoom(1)
  }

  // ==================== Private ====================

  _setZoom(value) {
    this._zoom = Math.round(value * 100) / 100
    this.sliderTarget.value = String(this._zoom)
    const pct = `${Math.round(this._zoom * 100)}%`

    if (this.hasButtonLabelTarget) {
      this.buttonLabelTarget.textContent = pct
      this.buttonLabelTarget.classList.remove("hidden")
    }

    this.dispatch("change", { detail: { zoom: this._zoom }, bubbles: true })
  }

  _isOpen() {
    return this.hasPopupTarget && !this.popupTarget.classList.contains("hidden")
  }

  _open() {
    if (this.hasPopupTarget) this.popupTarget.classList.remove("hidden")
  }

  _close() {
    if (this.hasPopupTarget) this.popupTarget.classList.add("hidden")
  }
}
